/**
 * UPDATE_TAB command
 *
 * Update a tab's metadata (title) by path.
 */

import { type z } from 'zod';

import { ConditionalRenderingGroup } from '../../conditional-rendering/group/ConditionalRenderingGroup';
import { TabItem } from '../../scene/layout-tabs/TabItem';
import {
  deserializeSectionVariables,
  serializeSectionVariables,
} from '../../serialization/layoutSerializers/sectionVariables';

import { resolveLayoutPath } from './layoutPathResolver';
import { payloads } from './schemas';
import { enterEditModeIfNeeded, requiresNewDashboardLayouts, type MutationCommand } from './types';
import { isSectionVariablesFeatureEnabled } from './variableScope';

const updateTabPayloadSchema = payloads.updateTab;

export type UpdateTabPayload = z.infer<typeof updateTabPayloadSchema>;

export const updateTabCommand: MutationCommand<UpdateTabPayload> = {
  name: 'UPDATE_TAB',
  description: payloads.updateTab.description ?? '',

  payloadSchema: payloads.updateTab,
  permission: requiresNewDashboardLayouts,
  readOnly: false,

  handler: async (payload, context) => {
    const { scene } = context;
    enterEditModeIfNeeded(scene);

    try {
      const { path, spec } = payload;

      const resolved = resolveLayoutPath(scene.state.body, path);
      if (!(resolved.item instanceof TabItem)) {
        throw new Error(`Path "${path}" does not point to a tab`);
      }

      const tab = resolved.item;
      const previousValue = {
        title: tab.state.title,
        conditionalRendering: tab.state.conditionalRendering?.serialize(),
        variables: serializeSectionVariables(tab.state.$variables),
      };

      const updates: Record<string, unknown> = {};
      if (spec.title !== undefined) {
        updates.title = spec.title;
      }

      tab.setState(updates);

      if (spec.repeat !== undefined) {
        tab.onChangeRepeat(spec.repeat?.value || undefined);
      }

      if (spec.conditionalRendering !== undefined) {
        const group = ConditionalRenderingGroup.deserialize(spec.conditionalRendering);
        tab.setState({ conditionalRendering: group });
      }

      if (spec.variables !== undefined && isSectionVariablesFeatureEnabled()) {
        tab.setState({ $variables: deserializeSectionVariables(spec.variables) });
      }

      const currentSpec = {
        title: tab.state.title,
        conditionalRendering: tab.state.conditionalRendering?.serialize(),
        variables: serializeSectionVariables(tab.state.$variables),
      };

      return {
        success: true,
        data: { path, tab: { kind: 'TabsLayoutTab', spec: currentSpec } },
        changes: [{ path, previousValue, newValue: currentSpec }],
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : String(error),
        changes: [],
      };
    }
  },
};
